import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path: string) => {
    setIsMenuOpen(false);
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isDashboardPage = location.pathname === '/dashboard';

  return (
    <header className="w-full border-b bg-white">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-md bg-brand-500 text-white flex items-center justify-center font-bold">TRB</span>
          <span className="text-xl font-semibold">The Resume Boost</span>
        </Link>

        {/* Desktop menu */}
        <nav className="hidden md:flex items-center gap-6">
          <Link to="/" className="text-sm font-medium hover:text-brand-500">Home</Link>
          <Link to="/about" className="text-sm font-medium hover:text-brand-500">How It Works</Link>
          <Link to="/pricing" className="text-sm font-medium hover:text-brand-500">Pricing</Link>
          <Link to="/contact" className="text-sm font-medium hover:text-brand-500">Contact</Link>
        </nav>

        <div className="hidden md:flex items-center gap-4">
          {user && session ? (
            <div className="flex items-center gap-4">
              {!isDashboardPage && (
                <Button onClick={() => handleNavigation('/dashboard')}>Go to Dashboard</Button>
              )}
              <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2">
                <LogOut className="h-4 w-4" />
                Logout
              </Button>
            </div>
          ) : (
            <>
              <Link to="/auth/login">
                <Button variant="outline">Log in</Button>
              </Link>
              <Link to="/auth/signup">
                <Button>Sign up</Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile menu button */}
        <button 
          className="md:hidden" 
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X /> : <Menu />}
        </button>
      </div>

      {/* Mobile menu drawer */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-50 bg-white pt-16">
          <nav className="container mx-auto px-4 py-8 flex flex-col gap-6">
            <button 
              onClick={() => handleNavigation('/')}
              className="text-lg font-medium hover:text-brand-500 text-left"
            >
              Home
            </button>
            <button 
              onClick={() => handleNavigation('/about')}
              className="text-lg font-medium hover:text-brand-500 text-left"
            >
              How It Works
            </button>
            <button 
              onClick={() => handleNavigation('/pricing')}
              className="text-lg font-medium hover:text-brand-500 text-left"
            >
              Pricing
            </button>
            <button 
              onClick={() => handleNavigation('/contact')}
              className="text-lg font-medium hover:text-brand-500 text-left"
            >
              Contact
            </button>
            <div className="flex flex-col gap-4 mt-6">
              {user && session ? (
                <>
                  {!isDashboardPage && (
                    <Button onClick={() => handleNavigation('/dashboard')} className="w-full">
                      Go to Dashboard
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    onClick={handleLogout} 
                    className="w-full flex items-center justify-center gap-2"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/auth/login" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="outline" className="w-full">Log in</Button>
                  </Link>
                  <Link to="/auth/signup" onClick={() => setIsMenuOpen(false)}>
                    <Button className="w-full">Sign up</Button>
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;

